import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone } from 'lucide-react';
import Navigation from '../components/home/Navigation';
import Footer from '../components/home/Footer';
import { VILLES, ZONES, villeUrl } from '../data/villes';
import { useSEO } from '../hooks/useSEO';
import { ENTREPRISE, HORAIRES_COURT, RDV_MENTION } from '../data/entreprise';

const ZonesInterventionPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useSEO({
    title: "Zones d'intervention – Réparation téléphone dans la Loire (42) | Suro Phone",
    description: `Réparateur iPhone et Samsung à domicile dans ${VILLES.length} communes de la Loire : Saint-Chamond, Saint-Étienne, Rive-de-Gier, Firminy, Montbrison, Roanne… Devis gratuit ☎ 07.73.60.15.21`,
    path: '/zones-intervention',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: "Communes desservies par Suro Phone dans la Loire (42)",
      itemListElement: VILLES.map((ville, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: ville.nom,
        url: `https://surophone.fr${villeUrl(ville)}`,
      })),
    },
  });

  const groupes = Object.entries(ZONES).map(([key, zone]) => ({
    key,
    ...zone,
    villes: VILLES.filter((x) => x.zone === key).sort((a, b) => a.nom.localeCompare(b.nom)),
  }));

  return (
    <div className="min-h-screen bg-white" data-testid="zones-page">
      <Navigation isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />

      <section className="bg-gradient-to-br from-[#1a2332] to-[#1e2a3d] text-white py-12 md:py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-blue-500/20 border border-blue-400 rounded-full px-4 py-2 mb-5 text-sm">
            <MapPin className="w-4 h-4" /> Département de la Loire (42)
          </div>
          <h1 className="text-3xl md:text-5xl font-bold mb-5 leading-tight">
            Réparation téléphone dans toute la Loire : {VILLES.length} communes desservies
          </h1>
          <p className="text-lg md:text-xl text-gray-300 mb-4">
            Basé à Saint-Chamond, Suro Phone répare votre iPhone ou Samsung à domicile ou à l'atelier, de la vallée du Gier au Roannais. Trouvez votre commune ci-dessous.
          </p>
          <p className="text-sm text-gray-400 mb-8" data-testid="zones-atelier-infos">Atelier : {ENTREPRISE.adresse} · {RDV_MENTION} · {HORAIRES_COURT}</p>
          <a href="tel:0773601521" data-testid="zones-call-btn" className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-xl font-bold text-lg">
            <Phone className="w-5 h-5" /> 07.73.60.15.21
          </a>
        </div>
      </section>

      <section className="py-12 md:py-16 px-4">
        <div className="max-w-5xl mx-auto space-y-12">
          {groupes.map((g) => (
            <div key={g.key} data-testid={`zone-group-${g.key}`}>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">{g.nom}</h2>
              <p className="text-gray-600 mb-5">Réparation iPhone et Samsung {g.texte}.</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {g.villes.map((ville) => (
                  <Link
                    key={ville.slug}
                    to={villeUrl(ville)}
                    data-testid={`zone-page-link-${ville.slug}`}
                    className="flex items-center justify-between gap-2 px-4 py-3 bg-gray-50 hover:bg-blue-50 border border-gray-100 hover:border-blue-400 rounded-xl transition-colors"
                  >
                    <span className="font-medium text-gray-900 text-sm">{ville.nom}</span>
                    <span className="text-xs text-gray-500 whitespace-nowrap">{ville.km} km</span>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-14 px-4 bg-blue-600 text-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-3">Votre commune n'est pas dans la liste ?</h2>
          <p className="text-blue-100 mb-6">Appelez-nous : nous nous déplaçons dans toute la Loire et les départements voisins sur demande.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a href="tel:0773601521" className="bg-white text-blue-600 px-8 py-4 rounded-xl font-bold text-lg">📞 07.73.60.15.21</a>
            <Link to="/rendez-vous" className="bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-xl font-bold text-lg">Prendre rendez-vous</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ZonesInterventionPage;
